import type { IntlShape } from 'react-intl';

import type { EmojiNodeDataProvider } from '@atlaskit/editor-plugin-emoji';
import type { UserPreferencesProvider as UserPreferencesProviderNext } from '@atlaskit/editor-common/user-preferences';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import type { GetPMNodeHeight } from '@atlaskit/editor-common/extensibility';
import type { FindReplaceOptions } from '@atlaskit/editor-plugins/find-replace';
import type { TaskDecisionProvider } from '@atlaskit/task-decision/types';
import type { MediaPluginOptions } from '@atlaskit/editor-plugins/media/types';
import type { ViewMode } from '@atlaskit/editor-plugin-editor-viewmode';
import type { EmojiProvider } from '@atlaskit/emoji';
import type { MentionProvider } from '@atlaskit/mention/resource';
import type {
	ContextIdentifierProvider,
	AutoformattingProvider,
} from '@atlaskit/editor-common/provider-factory';
import type { MentionPluginConfig } from '@atlaskit/editor-plugins/mentions';
import type { ExtensionHandlers } from '@atlaskit/editor-common/extensions';
import type { EventHandlers } from '@atlaskit/editor-common/ui';
import type { ExtensionPluginOptions } from '@atlaskit/editor-plugin-extension';
import type { LimitedModePluginOptions } from '@atlaskit/editor-plugin-limited-mode';
import type { LoomPluginOptionsWithoutProvider } from '@atlaskit/editor-plugin-loom';
import type { MentionsChangedHandler } from '@atlaskit/editor-plugin-mentions';
import type { EngagementPlatformPluginOptions } from '@atlaskit/editor-plugin-engagement-platform';
import type { ContextPanelPluginOptions } from '@atlaskit/editor-plugin-context-panel';
import type { SelectionExtensionPluginOptions } from '@atlaskit/editor-plugin-selection-extension';
import type { DiffParams } from '@atlaskit/editor-plugin-show-diff';
import type { SyncedBlockProvider } from '@atlaskit/editor-synced-block-provider';
import { getSyncedBlockRenderer } from '@atlaskit/editor-synced-block-renderer';
import type {
	UserPreferencesProvider,
	LinkingOptions,
	EmptyStateHandler,
	QuickInsertOptions,
	EditorContentMode,
} from '@atlaskit/editor-common/types';
import type { AnnotationProviders } from '@atlaskit/editor-plugins/annotation';
import type { CollabEditOptions } from '@atlaskit/editor-common/collab';
import type { TrackChangesPluginOptions } from '@atlaskit/editor-plugin-track-changes';
import { mediaEditingPlugin } from '@atlaskit/editor-plugin-media-editing';
import { markdownModePlugin } from '@atlassian/editor-plugin-markdown-mode';
import type { SerializeFileMedia } from '@atlassian/mdast-util-from-pm/handlers/media';

import {
	isMarkdownCompatibleQuickInsertItem,
	isMarkdownCompatibleToolbarComponent,
	isMarkdownCompatibleToolbarItem,
} from '@atlassian/editor-plugin-markdown-mode/markdown-compatibility';
import { nativeEmbedsPlugin } from '@atlassian/editor-plugin-native-embeds';

import type { AIDefinitionsPluginOptions } from '@atlassian/editor-plugin-ai-definitions';
import type { AIPluginOptions } from '@atlassian/editor-plugin-ai';
import { aiAutocompletePlugin } from '@atlassian/editor-plugin-ai-autocomplete';
import { malleableUiPlugin } from '@atlassian/editor-plugin-malleable-ui';
import { companyHubSectionCtaPlugin } from '@atlassian/editor-plugin-company-hub-section-cta';
import type { ReferentialityContext } from '@atlassian/editor-referentiality/types';
import { fullPagePreset } from '@atlassian/confluence-presets/full-page';
import { forgeEditorPlugin } from '@atlassian/xen-editor-provider';

import type { FullPageAppearances } from '@confluence/content-appearance/entry-points/contentAppearanceTypes.ts';
import { CompetitorPrompt } from '@confluence/prompt-competitor-link/entry-points/PromptPopup.ts';
import { selectInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience.ts';
import { createInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/createInlineCommentCompoundExperience.ts';
import { legacyContentMacroPlugin } from '@confluence/experiment-legacy-content-macro-plugin';
import {
	readStoredMarkdownViewMode,
	writeStoredMarkdownViewMode,
} from '@confluence/content-markdown-mode/entry-points/markdownViewMode.ts';
import {
	expVal,
	expValEquals,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import { getUnsupportedContent } from '@confluence/fabric-extension-lib/entry-points/getUnsupportedContent.ts';
import { fabricExtensionManagerPlugin } from '@confluence/fabric-extension-manager-plugin';
import { fg } from '@confluence/feature-gating';
import { editorOfflineStoragePlugin } from '@confluence/editor-offline-storage/entry-points/editorOfflineStoragePlugin.ts';
import { EditorHeadingLinksPlugin } from '@confluence/editor-heading-links/entry-points/headingLinksPlugin.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { createRemixContentComponent } from '@confluence/malleable-ui-common/entry-points/EditorRemixCreator.ts';

import { conflictResolutionUIPlugin } from './plugins/conflictResolutionUIPlugin/index.ts';
import { emptyStatePlugin } from './plugins/emptyStatePlugin.ts';
import { ssrPlugin } from './plugins/ssrPlugin.ts';
import { pageTitleUtilsPlugin } from './plugins/pageTitleUtilsPlugin.ts';

/**
 * Wraps a consumer-supplied `QuickInsertOptions` with a Markdown-Mode
 * allowlist filter when the page is in Markdown Mode. Items whose `id` is
 * not on `MARKDOWN_COMPATIBLE_QUICK_INSERT_ITEM_IDS` (panels, status,
 * layouts, smart cards, media, etc.) are hidden from the `/` typeahead.
 * Composes with any caller-supplied filter via `&&`. No-op when not in
 * Markdown Mode. Exported for unit testing.
 */
export const applyMarkdownModeQuickInsertFilter = (
	quickInsert: QuickInsertOptions | undefined,
	isMarkdownMode: boolean,
): QuickInsertOptions | undefined => {
	if (!isMarkdownMode) {
		return quickInsert;
	}

	// Normalise to an object so we can spread + add the filter.
	const baseOptions: Exclude<QuickInsertOptions, boolean> =
		typeof quickInsert === 'object' && quickInsert !== null ? quickInsert : {};

	const callerFilter = baseOptions.itemFilter;
	const itemFilter: NonNullable<typeof callerFilter> = callerFilter
		? (item) => isMarkdownCompatibleQuickInsertItem(item) && callerFilter(item)
		: isMarkdownCompatibleQuickInsertItem;

	return { ...baseOptions, itemFilter };
};

/**
 * IMPORTANT: Don't use optional properties in this interface because it reduces the type safety.
 *            Use `undefined` instead to indicate that the property is not set.
 */
export interface CreateFullPageEditorPresetProps {
	__isRemixHidden?: boolean;
	__remixDisabledReason: 'archived' | 'version-history' | undefined;
	__livePage: boolean | undefined;
	__rendererExtensionOptions: ExtensionPluginOptions['__rendererExtensionOptions'] | undefined;
	allowFindReplace: boolean | FindReplaceOptions | undefined;
	annotationProviders: Pick<AnnotationProviders, 'annotationManager' | 'inlineComment'> | undefined;
	appearance: FullPageAppearances;
	autoformattingProvider: Promise<AutoformattingProvider> | undefined;
	collabEdit: CollabEditOptions | undefined;
	contentId: string | undefined;
	/** Whether this page is a Markdown Mode page. */
	isMarkdownMode: boolean | undefined;
	spaceKey: string | undefined;
	contextIdentifierProvider: Promise<ContextIdentifierProvider> | undefined;
	contextPanelOptions: Pick<ContextPanelPluginOptions, 'objectSideBar'> | undefined;
	editorEngagementPlatformPluginConfig: Pick<EngagementPlatformPluginOptions, 'coordinationClient'>;
	editorPluginAIDefinitionsConfig: AIDefinitionsPluginOptions;
	editorPluginAIDefinitionsEnabled: boolean;
	editorPluginAIOptions: AIPluginOptions;
	elementBrowser:
		| {
				emptyStateHandler?: EmptyStateHandler;
		  }
		| undefined;
	emojiProvider: Promise<EmojiProvider> | undefined;
	emojiNodeDataProvider: EmojiNodeDataProvider | undefined;
	extensionHandlers: ExtensionHandlers | undefined;
	flags: FlagsStateContainer | undefined;
	getExtensionHeight: GetPMNodeHeight | undefined;
	getHeadingLinkPath: () => string | null;
	handleMentionsChanged: MentionsChangedHandler;
	currentUserId: string | undefined;
	hasEditPermission: boolean | undefined;
	hasRequestedEditPermission: boolean | undefined;
	intl: IntlShape;
	isAIEnabledForContent: boolean;
	isCompanyHub: boolean;
	isDesktop: boolean;
	isLoomPluginEnabled: boolean;
	limitedModePluginOptions: LimitedModePluginOptions | undefined;
	linking: Pick<LinkingOptions, 'linkPicker' | 'smartLinks'> | undefined;
	loomPluginOptions: Pick<LoomPluginOptionsWithoutProvider, 'renderButton'>;
	media:
		| Pick<
				MediaPluginOptions,
				'provider' | 'syncProvider' | 'mediaViewerExtensions' | 'fallbackMediaNameFetcher'
		  >
		| undefined;
	mention: Pick<MentionPluginConfig, 'profilecardProvider'> | undefined;
	mentionProvider: Promise<MentionProvider> | undefined;
	prevAppearance: FullPageAppearances | undefined;
	quickInsert: QuickInsertOptions | undefined;
	referentialityContext: ReferentialityContext;
	requestToEditContent: (() => void) | undefined;
	selectionExtensionPluginOptions: SelectionExtensionPluginOptions;
	taskDecisionProvider: Promise<TaskDecisionProvider> | undefined;
	userPreferencesProvider: UserPreferencesProvider | undefined;
	userPreferencesProviderNext: UserPreferencesProviderNext | undefined;
	viewMode: ViewMode | undefined;
	contextualFormattingEnabled: boolean;
	disabled?: boolean;
	blockLinkHashPrefix: string | undefined;
	showDiffPluginParams: DiffParams | undefined;
	syncedBlockProvider: SyncedBlockProvider | undefined;
	syncedBlockEventHandlers: EventHandlers | undefined;
	disableFloatingToolbar: boolean | undefined;
	disableToolbar: boolean | undefined;
	trackChangesPluginOptions: TrackChangesPluginOptions | undefined;
	contentMode: EditorContentMode | undefined;
	enablePlaceholderLoadingSpinner?: boolean;
	isEmbeddedConfluence: boolean;
	isRovoLLMEnabled: boolean;
	duplicateMauiApp?: (
		appId: string,
		containerContentId: string,
		contentType?: string,
	) => Promise<{ id: string }>;
	contentType?: string;
	cloudId?: string;
	/**
	 * Resolves `media (type: "file")` ids to a public proxy URL for use by
	 * the markdown-mode source view. Supplied by the Confluence wrapper via
	 * `useFileMediaUrlSerializer`. Pass `undefined` to disable file media in
	 * markdown mode (it falls back to alt-text-only).
	 */
	serializeFileMediaForMarkdown?: SerializeFileMedia;
}

/**
 * This function creates a full page editor preset with the provided options.
 *
 * If you want to do any changes, first of all, read https://hello.atlassian.net/wiki/spaces/EDITOR/pages/4239829169/Editor+RFC+059+Editor+Presets+Package.
 *
 * HOW TO MAKE CHANGES:
 * 1. Do not use optional properties in the `CreateFullPageEditorPresetProps` interface.
 * 2. Keep this function as small and simple as possible. Pass only the necessary parameters to the plugin options:
 *   - If a plugin parameter doesn't need any data from Confluence (e.g. option is just a static value),
 *     define it directly in platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/{pluginName}Options.ts.
 *   - If a plugin parameter depends on Statsig experiment value, FG value,
 *     define it directly in platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/{pluginName}Options.ts.
 *   - Pass parameters from this function to the plugin options ONLY if there is NO OTHER WAY to do it directly in the plugin options.
 *   - Pass only NECESSARY parameters to the plugin options, AVOID passing the WHOLE OBJECT with all properties.
 *   - Do NOT USE SPREAD operator (`...props`) to pass plugin options, it can make the code less readable and harder to maintain.
 */
// FIXME: https://product-fabric.atlassian.net/browse/ED-25722
export function createFullPageEditorPreset(options: CreateFullPageEditorPresetProps): any {
	const { intl, appearance, prevAppearance } = options;

	const isNativeEmbeddedLiveDoc =
		!!options.__livePage &&
		!!options.isEmbeddedConfluence &&
		UNSAFE_expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true);

	const isMarkdownMode = options.isMarkdownMode ?? false;
	const quickInsertOptions = applyMarkdownModeQuickInsertFilter(
		options.quickInsert,
		isMarkdownMode,
	);

	// `prettier-ignore` keeps the long fluent chain below on its current
	// `return fullPagePreset(...)` form. Without it Prettier re-indents ~570
	// chained-method lines and explodes the diff for any unrelated change.
	// The chain is purely declarative configuration.
	// prettier-ignore
	return fullPagePreset({
		// @ts-expect-error — react-intl v5/v7 type mismatch, will be removed when platform upgrades to react-intl v7
		intl,
		isRovoLLMEnabled: options.isRovoLLMEnabled,
		pluginOptions: {
			contentFormat: expValEquals('confluence_compact_text_format', 'isEnabled', true)
				? {
						initialContentMode: options.contentMode ?? 'standard',
				  }
				: undefined,
			base: {
				__livePage: options.__livePage,
			},
			helpDialog: {
				imageUploadProviderExists: false,
				aiEnabled: options.isAIEnabledForContent ?? false,
			},
			hyperlink: {
				editorAppearance: appearance,
				linkPicker: options.linking?.linkPicker,
				onClickCallback: options.linking?.smartLinks?.onClickCallback,
			},
			quickInsert: {
				emptyStateHandler: options.elementBrowser?.emptyStateHandler,
				quickInsert: quickInsertOptions,
			},
			placeholder: {
				viewMode: options.viewMode,
				isAIEnabled: options.isAIEnabledForContent ?? false,
				// hide placeholder when (1) experiment enabled (2) not embedded editor & (3) user would see BPQA.
				// match BPQA conditions as defined by shouldRenderBPQA in FullPageEditorComponent
				// in this case, only isCompanyHub is not handled yet by the placeholder plugin
				isPlaceholderHidden:
					!options.isEmbeddedConfluence &&
					!options.isCompanyHub &&
					options.isRovoLLMEnabled &&
					(expValEquals('cwr_blank_object_experiment', 'cohort', 'variant1') ||
						expValEquals('cwr_blank_object_experiment', 'cohort', 'variant2')),
				enableLoadingSpinner: options.enablePlaceholderLoadingSpinner,
				isRovoLLMEnabled: options.isRovoLLMEnabled,
			},
			selection: {
				__livePage: options.__livePage,
			},
			editorDisabled: {
				disabled: options.disabled,
			},
			breakout: {
				editorAppearance: appearance,
			},
			expand: {
				editorAppearance: appearance,
				__livePage: options.__livePage,
			},
			annotation: {
				annotationManager: options.annotationProviders?.annotationManager,
				createCommentExperience: createInlineCommentCompoundExperience,
				selectCommentExperience: selectInlineCommentCompoundExperience,
				viewInlineCommentTraceUFOPress: () => traceUFOPress('view-inline-comment-action'),
			},
			media: {
				createCommentExperience: fg('confluence_fe_create_inline_comment_exp_coverage_2')
					? createInlineCommentCompoundExperience
					: undefined,
				editorAppearance: appearance,
				mediaViewerExtensions: options.media?.mediaViewerExtensions,
				fallbackMediaNameFetcher: options.media?.fallbackMediaNameFetcher,
			},
			mentions: {
				handleMentionsChanged: options.handleMentionsChanged,
				currentUserId: options.currentUserId,
			},
			table: {
				editorAppearance: appearance,
				prevEditorAppearance: prevAppearance,
			},
			tasksAndDecisions: {
				hasEditPermission: options.hasEditPermission,
				requestToEditContent: options.requestToEditContent,
				hasRequestedEditPermission: options.hasRequestedEditPermission,
			},
			collabEdit: {
				collabEdit: options.collabEdit,
				__livePage: options.__livePage,
			},
			// Remove the options.contextPanelOptions undefined checking after cleaning `platform_editor_ai_object_sidebar_injection` FG
			// and options.contextPanelOptions will be always defined.
			contextPanel: options.contextPanelOptions
				? {
						objectSideBar: options.contextPanelOptions.objectSideBar,
				  }
				: undefined,
			extension: {
				extensionHandlers: options.extensionHandlers,
				editorAppearance: appearance,
				__rendererExtensionOptions: options.__rendererExtensionOptions,
				getExtensionHeight: options.getExtensionHeight,
				getUnsupportedContent,
			},
			layout: {
				editorAppearance: appearance,
			},
			card: {
				editorAppearance: appearance,
				linkPicker: options.linking?.linkPicker,
				onClickCallback: options.linking?.smartLinks?.onClickCallback,
				__livePage: options.__livePage,
				CompetitorPrompt,
			},
			insertBlock: {
				editorAppearance: appearance,
				// In Markdown Mode, hide toolbar insert items whose underlying node
				// has no clean GFM round-trip (panels, layouts, dates, status,
				// mentions, emoji, decisions, expand, placeholder text, rich `media`).
				itemFilter: isMarkdownMode ? isMarkdownCompatibleToolbarItem : undefined,
				toolbarButtons: {
					emoji: { enabled: true, showAt: 'lg' },
					insert: { enabled: true, showAt: 'lg' },
					layout: { enabled: true, showAt: 'lg' },
					media: { enabled: true, showAt: 'lg' },
					mention: { enabled: true, showAt: 'lg' },
					table: { enabled: true, showAt: 'md' },
					taskList: { enabled: true, showAt: 'lg' },
				},
			},
			avatarGroup: {
				// Avatars are moved to Confluence codebase for Edit in Context
				// When Edit in Context is enabled primaryToolbarComponents is undefined
				// For more details please check
				// https://hello.atlassian.net/wiki/spaces/PCG/pages/2851572180/Editor+toolbar+for+live+pages+and+edit+in+context+projects
				collabEdit: options.collabEdit,
			},
			codeBidiWarning: {
				editorAppearance: appearance,
			},
			referentiality: {
				referentialityContext: options.referentialityContext,
			},
			ai: options.editorPluginAIOptions,
			loom: {
				renderButton: options.loomPluginOptions.renderButton,
			},
			editorViewMode: {
				viewMode: options.viewMode,
			},
			aiDefinitions: options.editorPluginAIDefinitionsConfig,
			selectionMarker: {
				__livePage: options.__livePage ?? false,
			},
			engagementPlatform: {
				coordinationClient: options.editorEngagementPlatformPluginConfig.coordinationClient,
			},
			selectionExtension: options.selectionExtensionPluginOptions,
			metrics:
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				expValEquals('platform_editor_controls', 'cohort', 'variant1')
					? { userPreferencesProvider: options.userPreferencesProvider }
					: undefined,
			primaryToolbar: {
				contextualFormattingEnabled: options.contextualFormattingEnabled,
			},
			selectionToolbar: {
				contextualFormattingEnabled: options.contextualFormattingEnabled,
				// In embedded live doc, the toolbar is always unpinned and the pin option is hidden
				disablePin: isNativeEmbeddedLiveDoc,
			},
			userPreferencesPlugin: {
				// In embedded live doc, force the toolbar to start unpinned regardless of any saved user preference
				initialToolbarDockingPosition: isNativeEmbeddedLiveDoc ? 'none' : undefined,
			},
			blockMenu: {
				getLinkPath: options.getHeadingLinkPath,
				blockLinkHashPrefix: options.blockLinkHashPrefix,
			},
			showDiff: options.showDiffPluginParams,
			syncedBlock:
				options.syncedBlockProvider && expValEquals('platform_synced_block', 'isEnabled', true)
					? {
							__livePage: options.__livePage,
							enableSourceCreation: true,
							syncedBlockRenderer: getSyncedBlockRenderer({
								syncBlockRendererOptions: {
									eventHandlers: options.syncedBlockEventHandlers,
									stickyHeaders: {
										show: true,
										offsetTop: 0,
									},
								},
							}),
							syncBlockDataProvider: options.syncedBlockProvider,
					  }
					: undefined,
			limitedMode: options.limitedModePluginOptions || {},
			toolbar: {
				contextualFormattingEnabled: 'controlled',
				// In Markdown Mode, filter the toolbar two ways:
				//
				//   1. In wysiwyg view: hide buttons / menus whose underlying
				//      mark or node has no clean GFM round-trip (text colour,
				//      highlight, alignment, underline, sub/super, mention,
				//      emoji, layout, media, synced block).
				//   2. In source / preview view: hide everything except the
				//      view-mode toggle itself, since the buttons would have
				//      no effect on the source / preview surfaces.
				//
				componentFilter: isMarkdownMode ? isMarkdownCompatibleToolbarComponent : undefined,
			},
			trackChanges: {
				ButtonWrapper: options.trackChangesPluginOptions?.ButtonWrapper,
			},
			aiSuggestions: {
				contentAri: `ari:cloud:confluence::page/${options.contentId}`,
			},
		},
		providers: {
			// In embedded live doc, we always want the toolbar to be unpinned regardless of saved preferences.
			// Passing undefined for both providers prevents any shared user preference state from affecting
			// the embedded live doc toolbar docking position.
			userPreferencesProvider: isNativeEmbeddedLiveDoc
				? undefined
				: options.userPreferencesProvider,
			userPreferencesProviderNext: isNativeEmbeddedLiveDoc
				? undefined
				: options.userPreferencesProviderNext,
			cardProvider: options.linking?.smartLinks?.provider,
			contextIdentifierProvider: options.contextIdentifierProvider,
			inlineCommentAnnotationProvider: options.annotationProviders?.inlineComment,
			mediaProvider: options.media?.provider,
			syncMediaProvider: options.media?.syncProvider,
			mentionProvider: options.mentionProvider,
			profilecardProvider: options.mention?.profilecardProvider,
			emojiProvider: options.emojiProvider,
			emojiNodeDataProvider: options.emojiNodeDataProvider,
			collabEditProvider: options.collabEdit?.provider,
			autoformattingProvider: options.autoformattingProvider,
			taskDecisionProvider: options.taskDecisionProvider,
		},
		enabledOptionalPlugins: {
			limitedMode: false,
			findReplace: Boolean(options.allowFindReplace),
			referentiality: Boolean(options.referentialityContext.plugin),
			loom: options.isLoomPluginEnabled,
			aiDefinitions: options.editorPluginAIDefinitionsEnabled,
			connectivity:
				options.isDesktop || expVal('platform_editor_offline_editing_web', 'isEnabled', false),
			metrics: fg('platform_editor_ease_of_use_metrics'),
			codeBlockAdvanced: true,
			selectionExtension: expValEquals('platform_editor_controls', 'cohort', 'variant1'),
			aiExperience: Boolean(options.editorPluginAIOptions.aiGlobalOptIn.status !== 'disabled'),
			interaction:
				Boolean(options?.__livePage) ||
				expValEquals('platform_editor_no_cursor_on_edit_page_init', 'isEnabled', true),
			userPreferences: fg('platform_editor_use_preferences_plugin'),
			trackChanges: fg('aifc_create_enabled'),
			showDiff: Boolean(options?.showDiffPluginParams) || fg('aifc_create_enabled'),
			toolbar:
				!options?.disableToolbar &&
				(fg('aifc_create_enabled') ||
					(options.editorPluginAIOptions.aiGlobalOptIn.status !== 'enabled' &&
						expValEquals('platform_editor_toolbar_aifc_confluence', 'isEnabled', true))),
			blockMenu: expValEquals('platform_editor_block_menu', 'isEnabled', true),
			localId: fg('platform_editor_adf_with_localid'),
			aiStreamingOrchestrator: fg('aifc_create_enabled'),
			syncedBlock: expValEquals('platform_synced_block', 'isEnabled', true),
			floatingToolbar: !options?.disableFloatingToolbar,
			codeBidiWarning: !expValEquals(
				'platform_editor_remove_bidi_char_warning',
				'isEnabled',
				true,
			),
			contentFormat: expValEquals('confluence_compact_text_format', 'isEnabled', true),
			uiControlRegistry: expValEquals('platform_editor_paste_actions_menu', 'isEnabled', true),
			aiSuggestions: expVal('platform_editor_ai_content_suggested_edits', 'isEnabled', false),
			aiSpeechToText: expValEquals('ai_speech_to_text_in_editor', 'isEnabled', true),
			aiImageGeneration: expValEquals('cc_page_experiences_editor_image_generation', 'isEnabled', true),
			collabEdit: true,
		},
	})
		.add(ssrPlugin)
		.add([
			EditorHeadingLinksPlugin,
			{
				getHeadingLinkPath: options.getHeadingLinkPath,
			},
		])
		.add(pageTitleUtilsPlugin)
		.add(fabricExtensionManagerPlugin)
		.add(forgeEditorPlugin)
		.maybeAdd(
			[editorOfflineStoragePlugin, { flags: options.flags }],
			options.isDesktop || expVal('platform_editor_offline_editing_web', 'isEnabled', false),
		)
		.maybeAdd(
			[
				conflictResolutionUIPlugin,
				{
					flags: options.flags,
					contentId: options.contentId,
					spaceKey: options.spaceKey,
				},
			],
			options.isDesktop || expVal('platform_editor_offline_editing_web', 'isEnabled', false),
		)
		.add(emptyStatePlugin)
		.add(legacyContentMacroPlugin)
		// `previewComponent` is intentionally omitted: the preview view is
		// disabled in the toggle UI today and not yet implemented in Confluence.
		// When it lands, supply a Confluence renderer here (it needs product
		// providers — SmartCard, Emoji, media feature flags — so it can't live
		// in the platform plugin).
		.maybeAdd(
			[
				markdownModePlugin,
				{
					isMarkdownMode,
					contentId: options.contentId ?? '',
					viewModePersistence: {
						getInitialView: readStoredMarkdownViewMode,
						onViewChange: writeStoredMarkdownViewMode,
					},
					// EDITOR-6636 — pass through the Confluence-supplied
					// `serializeFileMedia`. Builds tenant attachment-proxy URLs
					// (`/wiki/download/attachments/...`) for `media(type:file)`
					// so the markdown text remains credential-free and the
					// renderer auths the bytes via the viewer's session cookie.
					serializeFileMedia: options.serializeFileMediaForMarkdown,
				},
			],
			isMarkdownMode,
		)
		.maybeAdd(mediaEditingPlugin, expVal('platform_editor_add_image_editing', 'isEnabled', false))
		.maybeAdd(companyHubSectionCtaPlugin, options.isCompanyHub)
		.maybeAdd(
			[
				malleableUiPlugin,
				{
					__isRemixHidden: options.__isRemixHidden,
					__isRovoLLMEnabled: options.isRovoLLMEnabled,
					__remixDisabledReason: options.__remixDisabledReason,
					__livePage: options.__livePage,
					createRemixContentComponent,
					diagramPaste:
						options.cloudId && options.spaceKey
							? {
									getCurrentPageId: () => options.contentId,
									getCurrentContentType: () => options.contentType,
									duplicateMauiApp: options.duplicateMauiApp,
									finalizeAfterMove: async (
										contentId: string,
										parentPageId: string,
										conversationContext?: {
											conversationId: string;
											messageId: string;
											invocationId: string;
										},
									) => {
										const siteId = options.cloudId;
										// `options.spaceKey` is the human-readable space key (e.g.
										// "~70121a2f09606006e4cddb4d8beb963e42aca" for personal
										// spaces). Both finalize endpoints accept this value
										// under the `spaceKey` field.
										const spaceKey = options.spaceKey;

										// 1. When `aifc_finalize_convo_ai` is OFF, call the
										//    monolith finalize endpoint. This handles BOTH the
										//    cross-space reparent AND the recents/finalize bookkeeping.
										//    No frontend GraphQL move is needed.
										if (!fg('aifc_finalize_convo_ai')) {
											const monolithResponse = await fetch(
												`/gateway/api/ex/confluence/${siteId}/wiki/rest/internals/latest/smart-create/finalize`,
												{
													method: 'PUT',
													credentials: 'include',
													headers: { 'Content-Type': 'application/json' },
													body: JSON.stringify({
														id: contentId,
														type: 'whiteboard',
														spaceKey,
														parentId: parentPageId,
													}),
												},
											);
											if (!monolithResponse.ok) {
												throw new Error(
													`Monolith finalize failed with status ${monolithResponse.status}`,
												);
											}
											return;
										}

										// 2. When `aifc_finalize_convo_ai` is ON, call the convo-ai
										//    action endpoint. The convo-ai BE handles BOTH the
										//    cross-space move (with elevated privileges) AND the
										//    finalize.
										//
										//    Conversation context is required — without it we have no
										//    way to identify the action server-side and the BE move
										//    can't be triggered. This is expected for any Rovo MAUI
										//    link that doesn't carry the `_rc` query param (e.g. when
										//    the link was created before the encoder was deployed).
										if (!conversationContext) {
											throw new Error(
												'Cannot finalize: no conversation context found in the pasted link. ' +
													'The link may be from an older Rovo Chat session.',
											);
										}

										// The convo-ai BE expects the same shape that the working MAUI
										// embed finalize buttons send (see
										// platform/packages/ai-mate/conversation-assistant/src/ui/staging-area-modal/staging-area/finalize-area/maui-embed-finalize-buttons/index.tsx
										// and `mapConfluenceMessageToActionPayload` in
										// platform/packages/ai-mate/rovo-platform-ui-components/src/ui/confluence-object-finalize-popup/index.tsx).
										// That shape spreads the original `messageAction` (key,
										// invocationId, actionId, displayName, status, toolSource,
										// humanMessageId, confirmationMessage, executedData) and the
										// full `action.data` (document, title, contentId) — all needed
										// for BE validation.
										//
										// Since the URL `_rc` param only carries the action IDs (to
										// avoid bloating the URL with the full document ADF), we fetch
										// the conversation messages here and rebuild the payload with
										// the full action data before POSTing.
										const messagesResponse = await fetch(
											`/gateway/api/assist/rovo/v1/chat/conversation/${conversationContext.conversationId}/messages`,
											{
												method: 'GET',
												credentials: 'include',
												headers: {
													'x-product': 'confluence',
													'x-experience-id': 'ai-mate',
												},
											},
										);
										if (!messagesResponse.ok) {
											throw new Error(
												`Failed to fetch conversation messages: ${messagesResponse.status}`,
											);
										}
										const messagesPayload = await messagesResponse.json();
										const messages = Array.isArray(messagesPayload?.items)
											? messagesPayload.items
											: [];

										// Locate the message and the action that originated this
										// finalize (matched by messageId and invocationId).
										let matchedAction: Record<string, unknown> | undefined;
										for (const message of messages) {
											if (message?.id !== conversationContext.messageId) {
												continue;
											}
											const actions = Array.isArray(message?.actions) ? message.actions : [];
											matchedAction = actions.find(
												(action: Record<string, unknown>) =>
													action?.invocationId === conversationContext.invocationId,
											);
											if (matchedAction) {
												break;
											}
										}

										if (!matchedAction) {
											throw new Error(
												'Could not locate the originating action in the Rovo conversation. ' +
													'It may have been deleted or the conversation has expired.',
											);
										}

										const existingData =
											(matchedAction.data as
												| { metadata?: Record<string, unknown> }
												| undefined) ?? {};
										const existingMetadata = existingData.metadata ?? {};

										const actionResponse = await fetch(
											`/gateway/api/assist/rovo/v1/chat/conversation/${conversationContext.conversationId}/action`,
											{
												method: 'POST',
												credentials: 'include',
												headers: {
													'Content-Type': 'application/json;charset=UTF-8',
													'x-product': 'confluence',
													'x-experience-id': 'ai-mate',
												},
												body: JSON.stringify({
													context: {
														// Spread the original action so all required
														// fields (key, invocationId, actionId,
														// displayName, status, toolSource,
														// humanMessageId, confirmationMessage,
														// executedData, data.document, data.title,
														// data.contentId, ...) are preserved.
														...matchedAction,
														messageId: conversationContext.messageId,
														resolution: {
															type: 'confirm',
															message: 'finalize',
														},
														data: {
															...existingData,
															metadata: {
																...existingMetadata,
																contentId,
																parentId: parentPageId,
																// Send as spaceKey since
																// `options.spaceKey` is the
																// human-readable key, not a
																// numeric/ARI space id.
																spaceKey,
															},
														},
													},
												}),
											},
										);
										if (!actionResponse.ok) {
											throw new Error(
												`Convo-ai finalize action failed with status ${actionResponse.status}`,
											);
										}
									},
									resolveContentSpaceKey: async (contentId: string) => {
										try {
											const response = await fetch(
												`/wiki/rest/api/content/${contentId}?expand=space`,
												{ credentials: 'include' },
											);
											if (!response.ok) {
												return null;
											}
											const data = await response.json();
											return data?.space?.key ?? null;
										} catch {
											return null;
										}
									},
									targetSpaceKey: options.spaceKey,
							  }
							: undefined,
				},
			],
			UNSAFE_expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true) &&
				!options.isEmbeddedConfluence,
		)
		.maybeAdd(
			[nativeEmbedsPlugin],
			UNSAFE_expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true),
		)
		.maybeAdd(
			aiAutocompletePlugin,
			expVal('platform_editor_ai_headingautocomplete', 'isEnabled', false),
		);
}
